
















import Vue from 'vue';
import moment from 'moment';
import { ButtonModel, EntityType, ErrorModel } from '@/types';
import ExpiryModal from '@/components/shared/ExpiryModal.vue';
import { mapActions, mapState } from 'vuex';
import { Dictionary } from 'vue-router/types/router';

moment.locale('en-gb');

export default Vue.extend({
  name: 'EntityLockModal',
  props: {
    lastActivity: {
      type: Date,
      required: true
    },
    entityType: {
      type: Number,
      default: EntityType.WorkflowJob,
      validator(value) {
        return Object.values(EntityType).includes(value);
      }
    },
    storeNamespace: {
      type: String,
      default: 'workflowJobStore'
    },
    buttons: {
      type: Array as () => ButtonModel[],
      required: true
    },
    buttonRouteName: {
      type: String,
      default: 'WorkflowQueue'
    },
    buttonRouteParams: {
      type: Object as () => Dictionary<string>,
      required: false
    },
    buttonRouteText: {
      type: String,
      default: 'Return to queue'
    },
    returnRouteDesc: {
      type: String,
      default: 'queue'
    }
  },
  data() {
    return {
      now: moment(),
      nowIntervalId: 0
    };
  },
  methods: {
    ...mapActions({
      extendLock(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/extendLock`, payload);
      },
      setExtendingLock(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setExtendingLock`, payload);
      },
      setComponentName(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setComponentName`, payload);
      },
      setErrorModel(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setErrorModel`, payload);
      },
      setIsDirty(dispatch, payload) {
        return dispatch(`${this.storeNamespace}/setIsDirty`, payload);
      }
    }),
    onExtendEntityLock(this: any) {
      this.extendLock();
    },
    onButtonClicked(this: any, button: ButtonModel) {
      this.$emit('buttonClicked', button.id);
    },
    userActivity(this: any) {
      if (this.expiresInLessThanOneMinute && !this.expiresInLessThanFortySeconds && !this.extendingLock) {
        this.setExtendingLock(true);
        this.onExtendEntityLock();
      }
    },
    onExpired() {
      clearInterval(this.nowIntervalId);
      this.setIsDirty(false);

      const entityName = this.prettySpaceEntityType;

      this.showErrorComponent(
        `Your lock on this ${entityName} has expired`,
        `If you need to work on this ${entityName}, you can return to ` + `the ${this.returnRouteDesc} and reopen the ${entityName}`
      );
    },
    showErrorComponent(this: any, header: string, message: string) {
      this.setErrorModel({
        header,
        showHeader: true,
        message,
        icon: 'stopwatch',
        iconTheme: 'text-primary',
        showButton: true,
        buttonRouteName: this.buttonRouteName,
        buttonRouteParams: this.buttonRouteParams,
        buttonText: this.buttonRouteText
      } as ErrorModel);
      this.setComponentName('ErrorNotification');
    },
    setNowToNow() {
      this.now = moment();
    }
  },
  watch: {
    expirySecondsLeft(value: number) {
      if (value <= 0) {
        this.onExpired();
      }
    },
    lastActivity() {
      this.userActivity();
    }
  },
  computed: {
    ...mapState({
      extendingLock(state: any, getters: any) {
        return getters[`${this.storeNamespace}/extendingLock`];
      },
      entityLock(state: any, getters: any) {
        return getters[`${this.storeNamespace}/entityLock`];
      }
    }),
    expiresInLessThanOneMinute(): boolean {
      return this.expirySecondsLeft <= 60;
    },
    expiresInLessThanFortySeconds(): boolean {
      return this.expirySecondsLeft <= 40;
    },
    expirySecondsLeft(this: any): number {
      const expiryDate = moment(this.entityLock.expires);
      const diff = expiryDate.diff(this.now);
      return Math.ceil(diff / 1000);
    },
    prettySpaceEntityType(this: any) {
      const overrides = {
        [EntityType[EntityType.WorkflowJob]]: 'job'
      };

      const entityType = EntityType[this.entityType];

      if (overrides[entityType]) {
        return overrides[entityType];
      }

      let formatted = entityType.replace(/([a-z])([A-Z])/g, '$1 $2');
      formatted = formatted.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');

      return formatted.toLowerCase();
    },
    getTitle(this: any) {
      return `Your ${this.prettySpaceEntityType} lock is about to expire`;
    },
    getCaption(this: any) {
      return `Your lock on this ${this.prettySpaceEntityType} will expire in`;
    }
  },
  mounted(this: any) {
    this.nowIntervalId = setInterval(this.setNowToNow, 1000);
  },
  beforeDestroy(this: any) {
    clearInterval(this.nowIntervalId);
  },
  created(this: any) {
    this.instruction = 'You can extend the lock if you want to continue working on it';
    this.extendButtonCaption = 'Extend Lock';
  },
  components: {
    ExpiryModal
  }
});
